import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import { APIURL } from '../../../config';
import {
    Box,
    Typography,
    Card,
    CardContent,
    TextField,
    Grid,
    Chip,
    Paper
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as RechartsTooltip } from 'recharts';

const EmployeeTimeEntries = ({ employee }) => {
    const [timeEntries, setTimeEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment().endOf('month').format('YYYY-MM-DD'));
    const [pageSize, setPageSize] = useState(10);
    const [groupedData, setGroupedData] = useState([]);
    const [totalHours, setTotalHours] = useState(0);

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1', '#a4de6c'];

    useEffect(() => {
        if (employee?.emp_id) {
            fetchTimeEntries();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employee, startDate, endDate]);

    const fetchTimeEntries = async () => {
        try {
            setLoading(true);
            
            // TODO: Replace with actual API call
            // const response = await axios.get(APIURL + '/employees/time-entries', {
            //     params: {
            //         employee_id: employee.emp_id,
            //         start_date: startDate,
            //         end_date: endDate
            //     }
            // });
            // const entries = response.data.timeEntries || [];
            
            // Dummy data for demonstration
            const entries = [
                {
                    time_entry_id: 1,
                    entry_date: moment(startDate).add(1, 'days').format('YYYY-MM-DD'),
                    task_type: 'Visit',
                    hours: 8.0,
                    description: 'Visit',
                    customer_name: 'ABC Corporation',
                    created_at: moment().subtract(2, 'days').format('YYYY-MM-DD')
                },
                {
                    time_entry_id: 2,
                    entry_date: moment(startDate).add(2, 'days').format('YYYY-MM-DD'),
                    task_type: 'Training',
                    hours: 4.0,
                    description: 'Home Visit',
                    customer_name: null,
                    created_at: moment().subtract(3, 'days').format('YYYY-MM-DD')
                },
                {
                    time_entry_id: 3,
                    entry_date: moment(startDate).add(3, 'days').format('YYYY-MM-DD'),
                    task_type: 'Time Off',
                    hours: 8.0,
                    description: 'CBS Visit',
                    customer_name: null,
                    created_at: moment().subtract(5, 'days').format('YYYY-MM-DD')
                },
                {
                    time_entry_id: 4,
                    entry_date: moment(startDate).add(4, 'days').format('YYYY-MM-DD'),
                    task_type: 'IS Visit',
                    hours: 6.5,
                    description: 'Bug fixes and code review',
                    customer_name: 'XYZ Industries',
                    created_at: moment().subtract(1, 'days').format('YYYY-MM-DD')
                },
                {
                    time_entry_id: 5,
                    entry_date: moment(startDate).add(5, 'days').format('YYYY-MM-DD'),
                    task_type: 'Visit',
                    hours: 2.0,
                    description: 'Sprint planning and retrospective',
                    customer_name: null,
                    created_at: moment().format('YYYY-MM-DD')
                },
                {
                    time_entry_id: 6,
                    entry_date: moment(startDate).add(6, 'days').format('YYYY-MM-DD'),
                    task_type: 'Visit',
                    hours: 7.5,
                    description: 'API integration development',
                    customer_name: 'ABC Corporation',
                    created_at: moment().format('YYYY-MM-DD')
                },
                {
                    time_entry_id: 7,
                    entry_date: moment(startDate).add(8, 'days').format('YYYY-MM-DD'),
                    task_type: 'Phone Call',
                    hours: 3.0,
                    description: 'Timesheet and expense reporting',
                    customer_name: null,
                    created_at: moment().format('YYYY-MM-DD')
                },
                {
                    time_entry_id: 8,
                    entry_date: moment(startDate).add(9, 'days').format('YYYY-MM-DD'),
                    task_type: 'Training',
                    hours: 2.5,
                    description: 'Azure DevOps certification study',
                    customer_name: null,
                    created_at: moment().format('YYYY-MM-DD')
                },
                {
                    time_entry_id: 9,
                    entry_date: moment(startDate).add(10, 'days').format('YYYY-MM-DD'),
                    task_type: 'Home Visit',
                    hours: 8.0,
                    description: 'Database optimization and performance tuning',
                    customer_name: 'XYZ Industries',
                    created_at: moment().format('YYYY-MM-DD')
                },
                {
                    time_entry_id: 10,
                    entry_date: moment(startDate).add(11, 'days').format('YYYY-MM-DD'),
                    task_type: 'CBS Visit',
                    hours: 5.0,
                    description: 'Customer support and troubleshooting',
                    customer_name: 'Tech Solutions Ltd',
                    created_at: moment().format('YYYY-MM-DD')
                }
            ];
            
            setTimeEntries(entries);
            processGroupedData(entries);
        } catch (error) {
            console.error('Error fetching time entries:', error);
            setTimeEntries([]);
            setGroupedData([]);
            setTotalHours(0);
        } finally {
            setLoading(false);
        }
    };

    const processGroupedData = (entries) => {
        // Group by task type and sum hours
        const grouped = entries.reduce((acc, entry) => {
            const taskType = entry.task_type || 'Unspecified';
            const hours = parseFloat(entry.hours) || 0;
            
            if (!acc[taskType]) {
                acc[taskType] = {
                    name: taskType,
                    hours: 0,
                    count: 0
                };
            }
            
            acc[taskType].hours += hours;
            acc[taskType].count += 1;
            
            return acc;
        }, {});

        const groupedArray = Object.values(grouped).map(item => ({
            ...item,
            hours: parseFloat(item.hours.toFixed(2)),
            value: parseFloat(item.hours.toFixed(2)) // For pie chart
        }));

        // Sort by hours descending
        groupedArray.sort((a, b) => b.hours - a.hours);

        setGroupedData(groupedArray);
        setTotalHours(groupedArray.reduce((sum, item) => sum + item.hours, 0));
    };

    const columns = [
        {
            field: 'entry_date',
            headerName: 'DATE',
            width: 120,
            valueFormatter: (value) => {
                return value ? moment.utc(value).format('MM/DD/YYYY') : '-';
            }
        },
        {
            field: 'task_type',
            headerName: 'TASK TYPE',
            flex: 1,
            minWidth: 180
        },
        {
            field: 'hours',
            headerName: 'HOURS',
            width: 100,
            valueFormatter: (value) => {
                return value ? parseFloat(value).toFixed(2) : '0.00';
            }
        },
        {
            field: 'description',
            headerName: 'DESCRIPTION',
            flex: 1,
            minWidth: 250
        },
        {
            field: 'customer_name',
            headerName: 'CUSTOMER',
            width: 180
        },
        {
            field: 'created_at',
            headerName: 'CREATED',
            width: 120,
            valueFormatter: (value) => {
                return value ? moment(value).format('MM/DD/YYYY') : '-';
            }
        }
    ];

    const summaryColumns = [
        {
            field: 'name',
            headerName: 'TASK TYPE',
            flex: 1,
            minWidth: 150
        },
        {
            field: 'count',
            headerName: 'ENTRIES',
            flex: 0.5,
            align: 'center',
            headerAlign: 'center'
        },
        {
            field: 'hours',
            headerName: 'TOTAL HOURS',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: (value) => {
                return value ? `${value.toFixed(2)} hrs` : '0.00 hrs';
            }
        },
        {
            field: 'percentage',
            headerName: 'PERCENTAGE',
            width: 150,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const percentage = totalHours > 0 ? ((params.row.hours / totalHours) * 100).toFixed(1) : 0;
                return (
                    <Chip
                        label={`${percentage}%`}
                        color="primary"
                        size="small"
                        variant="outlined"
                    />
                );
            }
        }
    ];

    const rows = timeEntries.map((entry, index) => ({
        id: entry.time_entry_id || index,
        ...entry
    }));

    const summaryRows = groupedData.map((item, index) => ({
        id: index,
        ...item
    }));

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0];
            const percentage = totalHours > 0 ? ((data.value / totalHours) * 100).toFixed(1) : 0;
            return (
                <Paper sx={{ p: 1.5 }}>
                    <Typography variant="body2" fontWeight="bold">{data.name}</Typography>
                    <Typography variant="body2">{data.value.toFixed(2)} hours</Typography>
                    <Typography variant="body2">{percentage}%</Typography>
                    <Typography variant="caption" color="textSecondary">{data.payload.count} entries</Typography>
                </Paper>
            );
        }
        return null;
    };

    return (
        <Box sx={{ px: 2, py: 2 }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Time Entries for {employee?.emp_first_name} {employee?.emp_last_name}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center' }}>
                    <TextField
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                    />
                    <TextField
                        label="End Date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                    />
                    <Box sx={{ ml: 2 }}>
                        <Chip 
                            label={`Total: ${totalHours.toFixed(2)} hours`} 
                            color="primary" 
                            sx={{ fontWeight: 'bold' }}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Summary Section */}
            {groupedData.length > 0 && (
                <Box sx={{ display: 'flex', gap: 3, mb: 3, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch' }}>
                    <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 calc(45% - 12px)' }, minWidth: 0 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Time Distribution by Task Type
                                </Typography>
                                <Box sx={{ height: 350, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={groupedData}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={(entry) => `${entry.name}: ${entry.hours}h`}
                                                outerRadius={100}
                                                fill="#8884d8"
                                                dataKey="value"
                                            >
                                                {groupedData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <RechartsTooltip content={<CustomTooltip />} />
                                            <Legend />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                    
                    <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(55% - 12px)' }, minWidth: 0 }}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="h6" gutterBottom>
                                    Summary by Task Type
                                </Typography>
                                <Box sx={{ flex: 1 }}>
                                    <DataGridPro
                                        rows={summaryRows}
                                        columns={summaryColumns}
                                        hideFooter
                                        disableSelectionOnClick
                                        initialState={{ density: 'compact' }}
                                        sx={{ border: 'none', height: '100%' }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                </Box>
            )}

            {/* Detailed Time Entries */}
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Detailed Time Entries
                    </Typography>
                    {timeEntries.length === 0 && !loading ? (
                        <Typography variant="body1" color="textSecondary" align="center" sx={{ py: 4 }}>
                            No time entries found for the selected date range.
                        </Typography>
                    ) : (
                        <DataGridPro
                            rows={rows}
                            columns={columns}
                            loading={loading}
                            pageSize={pageSize}
                            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                            pageSizeOptions={[10, 25, 50, 100]}
                            pagination
                            autoHeight
                            disableSelectionOnClick
                            initialState={{ density: 'compact' }}
                            sx={{ borderRadius: 1 }}
                        />
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default EmployeeTimeEntries;
